import { Alert, Collapse, Fade } from 'react-bootstrap'
import styles from './Notification.module.css'
import { JSX, useEffect, useState } from 'react'

export type AlertPosition = "top-right" | "top-left" | "bottom-right" | "bottom-left"
export type AlertType = "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark"

export type AlertItem = {
    id: number | string
    type: AlertType
    title?: string
    message: string
    duration?: number
}

type AnimationType = typeof Fade | typeof Collapse;

interface notificationPropsInterface {
    position: AlertPosition
    alerts: AlertItem[]
    dismiss: React.Dispatch<React.SetStateAction<AlertItem[]>>
    as: AnimationType
}

const DEFAULT_DURATION = 3000 // in ms

export default function Notification(props: notificationPropsInterface) {
    const { position, alerts, dismiss, as } = props

    if(alerts.length === 0) {
        return <></>
    }

    const dismissAlert = (id: string | number) => {
        dismiss(prev => prev.filter(x => x.id !== id))
    }

    const closeAlert = (id: string | number) => {
        dismiss(prev => prev.map(x => x.id === id ? { ...x, show: false } : x))
    }

    return (
        <div className={ `${ styles.alert } ${ styles[position] }` }>
            {
                alerts.slice().reverse().map(alert => {
                    let duration = alert.duration ?? DEFAULT_DURATION
                    if(duration < 0) {
                        duration = DEFAULT_DURATION
                    }

                    return (    
                        <AutoDismiss
                            key={ `notification-alert-${ alert.id }` }
                            onExited={ () => dismissAlert(alert.id) }
                            as={ as }
                            duration={ duration }
                        >
                            <div>
                                <Alert
                                    variant={ alert.type }
                                    dismissible
                                    onClose={ () => closeAlert(alert.id) }
                                >
                                    {
                                        alert.title && alert.title.length > 0 && (
                                            <>
                                                <h5>
                                                    <b dangerouslySetInnerHTML={{ __html: alert.title }} />
                                                </h5>
                                                <hr />
                                            </>
                                        )
                                    }

                                    <b>
                                        <span>{ alert.message }</span>
                                    </b>
                                </Alert>
                            </div>
                        </AutoDismiss>
                    )
                })
            }
        </div>
    )
}

interface autoDismissProps {
    as: AnimationType
    duration?: number
    children: JSX.Element
    onExited: () => void
}

function AutoDismiss(props: autoDismissProps) {
    const [show, setShow] = useState(true)

    const { as: Component, duration, children, onExited } = props

    useEffect(() => {
        const timer = setTimeout(() => setShow(false), duration)
        return () => clearTimeout(timer)
    }, [duration])
    
    return (
        <div style={{ width: "fit-content" }}>
            <Component in={show} onExited={ onExited }>
                {children}
            </Component>
        </div>
    )
}